import moment from "moment";

export const SPREADSHEET_DATE_DISPLAY_FORMAT = "MM/DD/YYYY";

/**
 * Format string for react-datepicker (uses date-fns internally).
 * date-fns requires lowercase 'dd' and 'yyyy' (not 'DD' and 'YYYY').
 */
export const SPREADSHEET_DATE_PICKER_FORMAT = "MM/dd/yyyy";


export type SpreadsheetDateValidationResult = {
  isValid: boolean;
  /** Normalized API value in YYYY/MM/DD format, or empty string when clearing */
  normalizedValue?: string;
};

export const validateDateInput = (rawValue: string) => {
  if (rawValue && rawValue.trim() !== "") {
    const validation = validateSpreadsheetDateInput(rawValue);
    if (!validation.isValid) {
      // Show error message for invalid date format
      return false;
    }
  }
  return true;
};
/**
 * Validate spreadsheet date input.
 * - Accepts only MM/DD/YYYY with 4-digit year.
 * - Returns normalized value in YYYY/MM/DD for API calls.
 * 
 * Validation steps:
 * 1. Check for empty string (valid - means clear date)
 * 2. Use moment.js strict parsing to enforce exact format matching
 * 3. Verify the date is valid (catches invalid calendar dates like Feb 30)
 * 4. Verify parsed components match input (ensures no normalization occurred)
 */
export const validateSpreadsheetDateInput = (
  raw: string
): SpreadsheetDateValidationResult => {
  // Handle empty/whitespace input
  const value = raw?.trim?.() ?? "";
  if (value === "") {
    return { isValid: true, normalizedValue: "" };
  }

  // Step 1: Extract and validate input components first
  const parts = value.split("/");
  if (parts.length !== 3) {
    return { isValid: false };
  }

  // Parse input components
  const inputMonth = parseInt(parts[0]!, 10);
  const inputDay = parseInt(parts[1]!, 10);
  const inputYear = parseInt(parts[2]!, 10);

  // Validate numeric values are valid
  if (isNaN(inputMonth) || isNaN(inputDay) || isNaN(inputYear)) {
    return { isValid: false };
  }

  // Step 2: Use moment's strict UTC parsing to validate format AND date validity
  // Use UTC to avoid timezone issues that could shift the date
  // Third parameter 'true' enables strict mode - requires exact format match
  // Strict mode will reject:
  //   - 1/21/2026 (missing leading zero)
  //   - 01/21/26 (2-digit year)
  //   - 01/21/202 (3-digit year)
  //   - 01-21-2026 (wrong separator)
  const date = moment.utc(value, "MM/DD/YYYY", true);
  
  // Step 3: Check if moment successfully parsed the date with strict format matching
  if (!date.isValid()) {
    return { isValid: false };
  }

  // Step 4: Get parsed date components (moment.month() is 0-indexed, so add 1)
  // Using UTC ensures no timezone shifts occur
  const parsedMonth = date.month() + 1;
  const parsedDay = date.date();
  const parsedYear = date.year();

  // Step 5: Verify parsed date matches input exactly
  // If they don't match, moment normalized an invalid date (e.g., Feb 30 -> Mar 2)
  if (parsedMonth !== inputMonth || parsedDay !== inputDay || parsedYear !== inputYear) {
    return { isValid: false };
  }

  // Step 6: Format as YYYY/MM/DD for API using UTC (already in UTC)
  const normalizedValue = date.format("YYYY/MM/DD");

  return {
    isValid: true,
    normalizedValue,
  };
};

export const getSpreadsheetInvalidDateMessage = (): string =>
  "Invalid date format. Dates must be entered as MM/DD/YYYY, for example: 01/21/2026.";


